import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Mail, CalendarDays, Trash2, LogOut, RefreshCw, Lock, ArrowLeft, Inbox as InboxIcon,
    Search, BellRing, FileEdit, X, Images, ClipboardList, ArrowUpDown, Send,
} from "lucide-react";
import { Monogram } from "./Monogram";
import { GalleryManager } from "./GalleryManager";
import {
    TEMPLATE_LABELS, loadTemplates, saveTemplates, matchTemplateKey, fillTemplate,
} from "./replyTemplates";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "ebl_admin_token";
const STATUSES = ["new", "read", "replied", "booked"];

const formatApiErrorDetail = (detail) => {
    if (detail == null) return "Something went wrong. Please try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail))
        return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
};

const inputCls =
    "w-full border border-gold/30 bg-ivory/5 px-5 py-4 text-sm text-ivory placeholder:text-ivory/40 outline-none transition-[border-color,box-shadow] duration-500 focus:border-gold focus:shadow-[0_0_0_3px_rgba(201,162,39,0.18)]";

const fmtDate = (iso) =>
    new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

const todayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const isOverdue = (inq) => !!inq.reminder_at && !["replied", "booked"].includes(inq.status) && inq.reminder_at <= todayStr();

const StatusPill = ({ status }) => {
    const styles = {
        new: "bg-gold text-emerald-deep",
        read: "border border-ivory/30 text-ivory/70",
        replied: "border border-gold/60 text-gold",
        booked: "bg-blushdeep text-emerald-deep",
    };
    return (
        <span data-testid={`status-pill-${status}`} className={`px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] ${styles[status] || styles.new}`}>
            {status}
        </span>
    );
};

const Login = ({ onLogin }) => {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            const { data } = await axios.post(`${API}/auth/login`, { email, password });
            onLogin(data.access_token, data.admin);
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="flex min-h-screen items-center justify-center bg-emerald-deep px-6" data-testid="admin-login-page">
            <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-md border border-gold/25 bg-emerald/60 p-10 backdrop-blur-sm">
                <div className="mb-8 flex flex-col items-center gap-4">
                    <Monogram size={72} />
                    <h1 className="font-serif text-2xl tracking-wide text-ivory">Inquiry Inbox</h1>
                    <p className="font-script text-2xl text-gold">for Leemerh's eyes only</p>
                </div>
                <label htmlFor="admin-email" className="mb-2 block text-[10px] uppercase tracking-[0.35em] text-gold/80">Email</label>
                <input
                    id="admin-email"
                    data-testid="admin-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="admin@eventsbyleemerh.com"
                    className={inputCls}
                />
                <label htmlFor="admin-password" className="mb-2 mt-5 block text-[10px] uppercase tracking-[0.35em] text-gold/80">Password</label>
                <input
                    id="admin-password"
                    data-testid="admin-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className={inputCls}
                />
                {error && (
                    <p data-testid="admin-login-error" className="mt-4 text-sm text-blushdeep">{error}</p>
                )}
                <button
                    type="submit"
                    data-testid="admin-login-btn"
                    disabled={busy}
                    className="btn-sweep mt-7 flex w-full items-center justify-center gap-2 bg-gold px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.3em] text-emerald-deep transition-opacity duration-300 disabled:opacity-60"
                >
                    <Lock size={14} /> {busy ? "Unlocking…" : "Unlock Inbox"}
                </button>
                <Link to="/" data-testid="admin-back-to-site-link" className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ivory/50 transition-colors duration-300 hover:text-gold">
                    <ArrowLeft size={12} /> Back to site
                </Link>
            </form>
        </div>
    );
};

const TemplatesModal = ({ templates, onSave, onClose }) => {
    const [draft, setDraft] = useState(templates);
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-10" data-testid="templates-modal">
            <div className="max-h-full w-full max-w-2xl overflow-y-auto border border-gold/30 bg-emerald-deep p-8">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl text-ivory">Reply Templates</h2>
                    <button data-testid="templates-modal-close-btn" onClick={onClose} className="text-ivory/60 transition-colors duration-300 hover:text-gold">
                        <X size={20} />
                    </button>
                </div>
                <p className="mt-2 text-xs text-ivory/55">
                    Used to pre-fill the "Reply by Email" draft based on the enquiry's event type. Use <span className="text-gold">{"{{name}}"}</span> for the client's first name.
                </p>
                <div className="mt-6 flex flex-col gap-6">
                    {Object.keys(TEMPLATE_LABELS).map((key) => (
                        <div key={key}>
                            <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">{TEMPLATE_LABELS[key]}</label>
                            <textarea
                                data-testid={`template-textarea-${key}`}
                                rows={5}
                                value={draft[key] || ""}
                                onChange={(e) => setDraft((d) => ({ ...d, [key]: e.target.value }))}
                                className={inputCls}
                            />
                        </div>
                    ))}
                </div>
                <div className="mt-8 flex justify-end gap-3">
                    <button data-testid="templates-modal-cancel-btn" onClick={onClose} className="border border-ivory/25 px-6 py-3 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold">
                        Cancel
                    </button>
                    <button
                        data-testid="templates-modal-save-btn"
                        onClick={() => { onSave(draft); onClose(); }}
                        className="bg-gold px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-deep transition-opacity duration-300 hover:opacity-85"
                    >
                        Save Templates
                    </button>
                </div>
            </div>
        </div>
    );
};

const ComposeReplyModal = ({ draft, onClose }) => {
    const [subject, setSubject] = useState(draft.subject);
    const [body, setBody] = useState(draft.body);

    const openInEmailApp = () => {
        window.location.href = `mailto:${encodeURIComponent(draft.email)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        onClose();
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4 py-10" data-testid="compose-reply-modal">
            <div className="max-h-full w-full max-w-2xl overflow-y-auto border border-gold/30 bg-emerald-deep p-8">
                <div className="flex items-center justify-between">
                    <h2 className="font-serif text-2xl text-ivory">Reply to {draft.name}</h2>
                    <button data-testid="compose-reply-close-btn" onClick={onClose} className="text-ivory/60 transition-colors duration-300 hover:text-gold">
                        <X size={20} />
                    </button>
                </div>
                <p className="mt-2 text-xs text-ivory/55">
                    Edit the draft below, then open it in your email app to send to <span className="text-gold">{draft.email}</span>.
                </p>
                <label className="mt-6 mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Subject</label>
                <input
                    data-testid="compose-reply-subject-input"
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className={inputCls}
                />
                <label className="mt-5 mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Message</label>
                <textarea
                    data-testid="compose-reply-body-textarea"
                    rows={9}
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    className={inputCls}
                />
                <div className="mt-8 flex justify-end gap-3">
                    <button data-testid="compose-reply-cancel-btn" onClick={onClose} className="border border-ivory/25 px-6 py-3 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold">
                        Cancel
                    </button>
                    <button
                        data-testid="compose-reply-open-email-btn"
                        onClick={openInEmailApp}
                        className="flex items-center gap-2 bg-gold px-6 py-3 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-deep transition-opacity duration-300 hover:opacity-85"
                    >
                        <Send size={13} /> Open in Email App
                    </button>
                </div>
            </div>
        </div>
    );
};

const Inbox = ({ token, admin, onLogout, tabBar }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [query, setQuery] = useState("");
    const [sortBy, setSortBy] = useState("received");
    const [templates, setTemplates] = useState(() => loadTemplates());
    const [showTemplates, setShowTemplates] = useState(false);
    const [composing, setComposing] = useState(null);
    const [expandedBooking, setExpandedBooking] = useState(() => new Set());
    const headers = { Authorization: `Bearer ${token}` };

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/inquiries`, { headers });
            setItems(data);
        } catch (err) {
            if (err.response?.status === 401) onLogout();
            else toast.error("Could not load inquiries — please try again.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    const cycleStatus = async (inq) => {
        const next = STATUSES[(STATUSES.indexOf(inq.status || "new") + 1) % STATUSES.length];
        try {
            await axios.patch(`${API}/inquiries/${inq.id}`, { status: next }, { headers });
            setItems((list) => list.map((i) => (i.id === inq.id ? { ...i, status: next } : i)));
        } catch {
            toast.error("Could not update status.");
        }
    };

    const remove = async (inq) => {
        if (!window.confirm(`Delete the inquiry from ${inq.name}? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/inquiries/${inq.id}`, { headers });
            setItems((list) => list.filter((i) => i.id !== inq.id));
            toast.success("Inquiry removed.");
        } catch {
            toast.error("Could not delete inquiry.");
        }
    };

    const setReminder = async (inq, dateStr) => {
        try {
            await axios.patch(`${API}/inquiries/${inq.id}/reminder`, { reminder_at: dateStr || null }, { headers });
            setItems((list) => list.map((i) => (i.id === inq.id ? { ...i, reminder_at: dateStr || null } : i)));
            toast.success(dateStr ? "Follow-up reminder set." : "Reminder cleared.");
        } catch {
            toast.error("Could not update reminder.");
        }
    };

    const saveTemplateSet = (draft) => {
        setTemplates(draft);
        saveTemplates(draft);
        toast.success("Reply templates saved.");
    };

    const openCompose = (inq) => {
        const key = matchTemplateKey(inq.event_type);
        setComposing({
            name: inq.name,
            email: inq.email,
            subject: `Your ${inq.event_type} enquiry — Events by Leemerh`,
            body: fillTemplate(templates[key], inq.name),
        });
    };

    const updateBooking = async (inq, patch) => {
        setItems((list) => list.map((i) => (i.id === inq.id ? { ...i, ...patch } : i)));
        try {
            await axios.patch(`${API}/inquiries/${inq.id}/booking`, patch, { headers });
        } catch {
            toast.error("Could not save booking details.");
        }
    };

    const toggleBooking = (id) => {
        setExpandedBooking((set) => {
            const next = new Set(set);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const counts = STATUSES.reduce((acc, s) => ({ ...acc, [s]: items.filter((i) => (i.status || "new") === s).length }), {});
    counts.due = items.filter(isOverdue).length;
    const statusFiltered =
        filter === "all" ? items : filter === "due" ? items.filter(isOverdue) : items.filter((i) => (i.status || "new") === filter);
    const q = query.trim().toLowerCase();
    const searched = q
        ? statusFiltered.filter((i) => `${i.name} ${i.email} ${i.event_type}`.toLowerCase().includes(q))
        : statusFiltered;
    const visible = sortBy === "event"
        ? [...searched].sort((a, b) => {
            if (!a.event_date && !b.event_date) return 0;
            if (!a.event_date) return 1;
            if (!b.event_date) return -1;
            return new Date(a.event_date) - new Date(b.event_date);
        })
        : searched;
    const nextLabel = { new: "Mark as Read", read: "Mark as Replied", replied: "Mark as Booked", booked: "Move to New" };

    return (
        <div className="min-h-screen bg-emerald-deep px-6 py-12 lg:px-10" data-testid="admin-inbox">
            <div className="mx-auto max-w-5xl">
                <div className="flex flex-wrap items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <Monogram size={56} />
                        <div>
                            <h1 className="font-serif text-2xl tracking-wide text-ivory sm:text-3xl">Inquiry Inbox</h1>
                            <p className="text-[10px] uppercase tracking-[0.3em] text-gold/70">Signed in as {admin.email}</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Link to="/" data-testid="inbox-view-site-link" className="border border-ivory/25 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold">
                            View Site
                        </Link>
                        <button
                            data-testid="inbox-templates-btn"
                            onClick={() => setShowTemplates(true)}
                            className="flex items-center gap-2 border border-ivory/25 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            <FileEdit size={12} /> Reply Templates
                        </button>
                        <button
                            data-testid="inbox-refresh-btn"
                            onClick={load}
                            className="flex items-center gap-2 border border-ivory/25 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold"
                        >
                            <RefreshCw size={12} /> Refresh
                        </button>
                        <button
                            data-testid="inbox-logout-btn"
                            onClick={onLogout}
                            className="flex items-center gap-2 border border-gold/50 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-deep"
                        >
                            <LogOut size={12} /> Logout
                        </button>
                    </div>
                </div>

                {tabBar}

                <div className="mt-8 flex flex-wrap items-center gap-4">
                    <div className="relative w-full max-w-sm">
                        <Search size={14} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-gold/60" />
                        <input
                            data-testid="inbox-search-input"
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder="Search by name, email or event type…"
                            className={`${inputCls} pl-11`}
                        />
                    </div>
                    <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-ivory/60">
                        <ArrowUpDown size={13} className="text-gold" /> Sort:
                        <button
                            data-testid="sort-received-btn"
                            onClick={() => setSortBy("received")}
                            className={`px-4 py-2 ${sortBy === "received" ? "bg-gold text-emerald-deep" : "border border-gold/30 text-gold/80 hover:border-gold"}`}
                        >
                            Newest Received
                        </button>
                        <button
                            data-testid="sort-event-btn"
                            onClick={() => setSortBy("event")}
                            className={`px-4 py-2 ${sortBy === "event" ? "bg-gold text-emerald-deep" : "border border-gold/30 text-gold/80 hover:border-gold"}`}
                        >
                            Upcoming Event
                        </button>
                    </div>
                </div>

                <div className="mt-6 flex flex-wrap gap-3" data-testid="inbox-filter-tabs">
                    {["all", ...STATUSES, "due"].map((f) => (
                        <button
                            key={f}
                            data-testid={`filter-tab-${f}`}
                            onClick={() => setFilter(f)}
                            className={`px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.3em] transition-colors duration-300 ${
                                filter === f ? "bg-gold text-emerald-deep" : "border border-gold/30 text-gold/80 hover:border-gold"
                            }`}
                        >
                            {f === "due" ? "Follow-up Due" : f} {f === "all" ? `(${items.length})` : `(${counts[f]})`}
                        </button>
                    ))}
                </div>

                <div className="mt-8 flex flex-col gap-5">
                    {loading ? (
                        <p className="py-16 text-center text-sm text-ivory/60" data-testid="inbox-loading">Gathering your letters…</p>
                    ) : visible.length === 0 ? (
                        <div className="flex flex-col items-center gap-4 border border-dashed border-gold/25 py-20" data-testid="inbox-empty">
                            <InboxIcon className="text-gold/50" size={36} strokeWidth={1.2} />
                            <p className="font-script text-3xl text-gold">No letters here yet</p>
                            <p className="text-sm text-ivory/55">
                                {q || filter !== "all" ? "No enquiries match your search or filter." : "New inquiries from the website will appear here."}
                            </p>
                        </div>
                    ) : (
                        visible.map((inq) => (
                            <article
                                key={inq.id}
                                data-testid={`inquiry-card-${inq.id}`}
                                className="border border-gold/20 bg-emerald/50 p-6 backdrop-blur-sm transition-colors duration-300 hover:border-gold/45 sm:p-8"
                            >
                                <div className="flex flex-wrap items-start justify-between gap-4">
                                    <div>
                                        <h3 className="font-serif text-xl tracking-wide text-ivory" data-testid={`inquiry-name-${inq.id}`}>{inq.name}</h3>
                                        <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-gold">{inq.event_type}</p>
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                        {isOverdue(inq) && (
                                            <span data-testid={`inquiry-followup-due-${inq.id}`} className="flex items-center gap-1 bg-blushdeep px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-deep">
                                                <BellRing size={11} /> Follow-up Due
                                            </span>
                                        )}
                                        <StatusPill status={inq.status || "new"} />
                                    </div>
                                </div>
                                <div className="mt-4 flex flex-wrap gap-x-8 gap-y-2 text-xs text-ivory/65">
                                    <a href={`mailto:${inq.email}`} data-testid={`inquiry-email-${inq.id}`} className="flex items-center gap-2 transition-colors duration-300 hover:text-gold">
                                        <Mail size={13} /> {inq.email}
                                    </a>
                                    {inq.event_date && (
                                        <span className="flex items-center gap-2">
                                            <CalendarDays size={13} /> Event: {fmtDate(inq.event_date)}
                                        </span>
                                    )}
                                    <span className="flex items-center gap-2 text-ivory/45">
                                        Received {fmtDate(inq.created_at)}
                                    </span>
                                </div>
                                <p className="mt-4 border-l-2 border-gold/40 pl-4 font-cormorant text-lg italic leading-relaxed text-ivory/85" data-testid={`inquiry-message-${inq.id}`}>
                                    "{inq.message}"
                                </p>
                                <div className="mt-6 flex flex-wrap items-center gap-3">
                                    <button
                                        onClick={() => openCompose(inq)}
                                        data-testid={`inquiry-reply-btn-${inq.id}`}
                                        title="Edit the templated reply, then open it in your email app."
                                        className="flex items-center gap-2 border border-gold bg-gold px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-emerald-deep transition-colors duration-300 hover:bg-gold/80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-gold"
                                    >
                                        <Mail size={12} aria-hidden="true" /> Reply by Email
                                    </button>
                                    <button
                                        data-testid={`inquiry-status-btn-${inq.id}`}
                                        onClick={() => cycleStatus(inq)}
                                        className="border border-gold/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-deep"
                                    >
                                        {nextLabel[inq.status || "new"]}
                                    </button>
                                    <label className="flex items-center gap-2 border border-ivory/25 px-4 py-2 text-[10px] uppercase tracking-[0.25em] text-ivory/60">
                                        <BellRing size={12} className="text-gold" />
                                        Follow-up
                                        <input
                                            type="date"
                                            data-testid={`inquiry-reminder-input-${inq.id}`}
                                            value={inq.reminder_at ? inq.reminder_at.slice(0, 10) : ""}
                                            onChange={(e) => setReminder(inq, e.target.value)}
                                            className="bg-transparent text-ivory outline-none [color-scheme:dark]"
                                        />
                                        {inq.reminder_at && (
                                            <button
                                                type="button"
                                                data-testid={`inquiry-reminder-clear-${inq.id}`}
                                                onClick={() => setReminder(inq, "")}
                                                className="text-ivory/50 transition-colors duration-300 hover:text-gold"
                                                aria-label="Clear reminder"
                                            >
                                                <X size={12} />
                                            </button>
                                        )}
                                    </label>
                                    <button
                                        data-testid={`inquiry-booking-toggle-${inq.id}`}
                                        onClick={() => toggleBooking(inq.id)}
                                        className="flex items-center gap-2 border border-ivory/25 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-ivory/60 transition-colors duration-300 hover:border-gold hover:text-gold"
                                    >
                                        <ClipboardList size={12} /> Booking Details
                                    </button>
                                    <button
                                        data-testid={`inquiry-delete-btn-${inq.id}`}
                                        onClick={() => remove(inq)}
                                        className="flex items-center gap-2 border border-blushdeep/40 px-5 py-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-blushdeep transition-colors duration-300 hover:bg-blushdeep hover:text-emerald-deep"
                                    >
                                        <Trash2 size={12} /> Delete
                                    </button>
                                </div>
                                {expandedBooking.has(inq.id) && (
                                    <div className="mt-4 grid grid-cols-1 gap-4 border-t border-gold/15 pt-4 sm:grid-cols-2" data-testid={`inquiry-booking-panel-${inq.id}`}>
                                        <div>
                                            <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Deposit</label>
                                            <input
                                                type="text"
                                                data-testid={`inquiry-deposit-input-${inq.id}`}
                                                defaultValue={inq.deposit || ""}
                                                placeholder="e.g. £500 paid 12 Jan"
                                                onBlur={(e) => updateBooking(inq, { deposit: e.target.value })}
                                                className={inputCls}
                                            />
                                        </div>
                                        <div>
                                            <label className="mb-2 block text-[10px] uppercase tracking-[0.3em] text-gold/80">Planning Notes</label>
                                            <textarea
                                                rows={2}
                                                data-testid={`inquiry-notes-input-${inq.id}`}
                                                defaultValue={inq.notes || ""}
                                                placeholder="Call outcomes, preferences, pricing discussed…"
                                                onBlur={(e) => updateBooking(inq, { notes: e.target.value })}
                                                className={inputCls}
                                            />
                                        </div>
                                    </div>
                                )}
                            </article>
                        ))
                    )}
                </div>
            </div>
            {showTemplates && (
                <TemplatesModal templates={templates} onSave={saveTemplateSet} onClose={() => setShowTemplates(false)} />
            )}
            {composing && (
                <ComposeReplyModal draft={composing} onClose={() => setComposing(null)} />
            )}
        </div>
    );
};

export const Admin = () => {
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
    const [admin, setAdmin] = useState(null);
    const [checking, setChecking] = useState(!!localStorage.getItem(TOKEN_KEY));
    const [tab, setTab] = useState("inquiries");

    useEffect(() => {
        if (!token) {
            setChecking(false);
            return;
        }
        axios
            .get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
            .then((r) => setAdmin(r.data))
            .catch(() => {
                localStorage.removeItem(TOKEN_KEY);
                setToken(null);
            })
            .finally(() => setChecking(false));
    }, [token]);

    if (checking) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-emerald-deep" data-testid="admin-loading">
                <span className="font-script text-3xl text-gold">One moment…</span>
            </div>
        );
    }

    if (!admin) {
        return (
            <Login
                onLogin={(t, a) => {
                    localStorage.setItem(TOKEN_KEY, t);
                    setToken(t);
                    setAdmin(a);
                }}
            />
        );
    }

    const onLogout = () => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setAdmin(null);
    };

    if (tab === "gallery") {
        return (
            <div className="min-h-screen bg-emerald-deep px-6 py-12 lg:px-10" data-testid="admin-gallery-page">
                <div className="mx-auto max-w-5xl">
                    <div className="flex flex-wrap items-center justify-between gap-6">
                        <div className="flex items-center gap-4">
                            <Monogram size={56} />
                            <div>
                                <h1 className="font-serif text-2xl tracking-wide text-ivory sm:text-3xl">Gallery Manager</h1>
                                <p className="text-[10px] uppercase tracking-[0.3em] text-gold/70">Signed in as {admin.email}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Link to="/" data-testid="gallery-view-site-link" className="border border-ivory/25 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-ivory/70 transition-colors duration-300 hover:border-gold hover:text-gold">
                                View Site
                            </Link>
                            <button data-testid="inbox-logout-btn" onClick={onLogout} className="flex items-center gap-2 border border-gold/50 px-5 py-2.5 text-[10px] uppercase tracking-[0.3em] text-gold transition-colors duration-300 hover:bg-gold hover:text-emerald-deep">
                                <LogOut size={12} /> Logout
                            </button>
                        </div>
                    </div>
                    <div className="mt-8 flex gap-3" data-testid="admin-tabs">
                        <button data-testid="admin-tab-inquiries" onClick={() => setTab("inquiries")} className="flex items-center gap-2 border border-gold/30 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold/80 transition-colors duration-300 hover:border-gold">
                            <InboxIcon size={13} /> Inquiries
                        </button>
                        <button data-testid="admin-tab-gallery" className="flex items-center gap-2 bg-gold px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-deep">
                            <Images size={13} /> Gallery
                        </button>
                    </div>
                    <div className="mt-8">
                        <GalleryManager token={token} />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <Inbox
            token={token}
            admin={admin}
            onLogout={onLogout}
            tabBar={
                <div className="mt-6 flex gap-3" data-testid="admin-tabs">
                    <button data-testid="admin-tab-inquiries" className="flex items-center gap-2 bg-gold px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-emerald-deep">
                        <InboxIcon size={13} /> Inquiries
                    </button>
                    <button data-testid="admin-tab-gallery" onClick={() => setTab("gallery")} className="flex items-center gap-2 border border-gold/30 px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.3em] text-gold/80 transition-colors duration-300 hover:border-gold">
                        <Images size={13} /> Gallery
                    </button>
                </div>
            }
        />
    );
};
